/**
 * explainError — turn any thrown error into copy a person can act on.
 *
 * Standing rule (2026-08-06): every failure surfaced to a user must say what
 * happened and what to do next. Raw axios strings ("Request failed with status
 * code 403"), bare status codes, and backend exception text are never shown.
 *
 * Server text policy:
 *  - 400/409/422: our API writes these for humans (validators, guards) — show it.
 *  - 429: show the limiter's descriptive title plus a concrete wait time.
 *  - 5xx: the body's `error` field is often a raw exception message — never show
 *    it; give a friendly retry/contact-support line instead.
 */

interface AxiosLikeError {
  code?: string
  message?: string
  response?: {
    status?: number
    data?: {
      error?: string
      message?: string
      retryAfter?: number
      retryAfterDate?: string
    }
  }
}

export function explainError(error: unknown, fallback = 'Something went wrong'): string {
  const ax = (error ?? {}) as AxiosLikeError
  const status = ax.response?.status
  const serverError = ax.response?.data?.error
  const serverMessage = ax.response?.data?.message

  // No HTTP response at all — the request never made it to the server.
  if (!status) {
    if (ax.code === 'ECONNABORTED' || /timeout/i.test(ax.message || '')) {
      return `${fallback}: the request timed out. Check your connection and try again.`
    }
    if (ax.code === 'ERR_NETWORK' || ax.message === 'Network Error') {
      return `${fallback}: we couldn't reach the server. Check your internet connection and try again.`
    }
    // Non-HTTP errors (client-side logic) — the message is our own copy.
    return ax.message || fallback
  }

  switch (status) {
    case 400:
    case 409:
    case 422:
      return (
        serverError ||
        serverMessage ||
        `${fallback}: the request wasn't valid. Check the form and try again.`
      )
    case 401:
      return 'Your session has expired. Please sign in again.'
    case 403:
      return "You don't have permission to do this. If you think you should, ask an administrator."
    case 404:
      // A 404 is not always a deleted record. Several endpoints use it to say something
      // specific and actionable — "No billings have been uploaded yet. Upload a QuickBooks
      // Net Billings export to get started." is a 404 — and this branch used to discard
      // that and assert a deletion instead, which sent people looking for a record that
      // had never existed. Prefer whatever the server said, exactly as 400/409/422 above.
      return (
        serverError ||
        serverMessage ||
        `${fallback}: the record couldn't be found — it may have been deleted.`
      )
    case 413:
      return 'That upload is too large. Try a smaller file.'
    case 429: {
      const secs = ax.response?.data?.retryAfter
      const mins = typeof secs === 'number' && secs > 0 ? Math.max(1, Math.ceil(secs / 60)) : null
      const wait = mins ? `about ${mins} minute${mins === 1 ? '' : 's'}` : 'a moment'
      return `${serverError || 'Too many requests'} — please wait ${wait} and try again.`
    }
    default:
      if (status >= 500) {
        return `${fallback}: something went wrong on our side. Try again — if it keeps happening, contact support.`
      }
      return serverError || serverMessage || fallback
  }
}
